import React, { useState, useMemo, useEffect } from "react";
import { LeadData, LeadType } from "../../types";
import DetailFields, {
  DetailFieldsContext,
  FieldsConfig
} from "@/components/layouts/DetailFields";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import {
  mapSectionsFieldsConfig,
  useEntityDetailFieldsLayout
} from "@/hooks/useEntityFieldsConfig";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import {
  useLeadInboundCampaignTypes,
  useLeadOutboundCampaignTypes
} from "../../hooks";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { getCampaignTypeOptions } from "./utils";
import {
  OTHER_COMPANY_OPTION_VALUE,
  OTHER_PERSON_OPTION_VALUE
} from "../CreateLeadModal";

interface Props {
  lead?: LeadData;
  onUpdateField: (
    field: string,
    newValue: string | number | string[],
    additionalFields?: { [fieldName: string]: string | number }
  ) => void;
  sources: any[];
  licenseTypes: any[];
  className?: string;
  containerClassName?: string;
  isDrawerOpen?: boolean;
  isQuickView?: boolean;
}

const CONDITIONAL_FIELD_KEYS = [
  "inboundCampaignType",
  "outboundCampaignType",
  "referringCompany",
  "referringPerson",
  "otherCompanyName",
  "otherPersonName"
];

const LeadDetailFields: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);
  const [selectedSource, setSelectedSource] = useState<LeadType | null>(null);
  const [selectedReferringCompanyId, setSelectedReferringCompanyId] =
    useState<string>("");
  const [isOtherReferringCompanySelected, setIsOtherReferringCompanySelected] =
    useState<boolean>(false);
  const [isOtherReferringPersonSelected, setIsOtherReferringPersonSelected] =
    useState<boolean>(false);

  const { data: entityFieldsResponse } = useEntityFields(EntityType.LEAD);
  const updateEntityField = useUpdateEntityField(EntityType.LEAD);
  const inboundCampaignTypes =
    useLeadInboundCampaignTypes().data?.inboundCampaignTypes ?? [];
  const outboundCampaignTypes =
    useLeadOutboundCampaignTypes().data?.outboundCampaignTypes ?? [];
  const {
    options: referringCompanyOptions,
    onSearch: onReferringCompanySearch,
    isLoading: isLoadingReferringCompanies,
    searchTerm: referringCompanySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    alwaysEnabled: selectedSource === LeadType.REFERRAL
  });

  const {
    options: referringPersonOptions,
    onSearch: onReferringPersonSearch,
    isLoading: isLoadingReferringPeople,
    searchTerm: referringPersonSearchTerm
  } = useEntitySelect({
    entityType: EntityType.PERSON,
    filters: { companyId: selectedReferringCompanyId }
  });

  useEffect(() => {
    if (props.lead?.source) {
      setSelectedSource(Number(props.lead.source) as LeadType);
    }
  }, [props.lead?.source]);

  useEffect(() => {
    if (props.lead?.referringCompany) {
      const isOther =
        props.lead.referringCompany === OTHER_COMPANY_OPTION_VALUE;
      setIsOtherReferringCompanySelected(isOther);
      setSelectedReferringCompanyId(isOther ? "" : props.lead.referringCompany);
    }
  }, [props.lead?.referringCompany]);

  useEffect(() => {
    if (props.lead?.referringPerson) {
      const isOther = props.lead.referringPerson === OTHER_PERSON_OPTION_VALUE;
      setIsOtherReferringPersonSelected(isOther);
    }
  }, [props.lead?.referringPerson]);

  const getInboundCampaignTypeOptions = () =>
    getCampaignTypeOptions(inboundCampaignTypes);
  const getOutboundCampaignTypeOptions = () =>
    getCampaignTypeOptions(outboundCampaignTypes);

  const getConditionalFields = useMemo(() => {
    const conditionalFields: any = {};

    if (selectedSource === LeadType.INBOUND) {
      const inboundOptions = getInboundCampaignTypeOptions();

      conditionalFields.inboundCampaignType = {
        label: t("leads.createModal.inboundCampaignType"),
        type: "select",
        options: inboundOptions
      };
    }

    if (selectedSource === LeadType.OUTBOUND) {
      const outboundOptions = getOutboundCampaignTypeOptions();

      conditionalFields.outboundCampaignType = {
        label: t("leads.createModal.outboundCampaignType"),
        type: "select",
        options: outboundOptions
      };
    }

    if (selectedSource === LeadType.REFERRAL) {
      const otherCompanyOption = {
        id: OTHER_COMPANY_OPTION_VALUE,
        displayName: t("other")
      };

      conditionalFields.referringCompany = {
        label: t("leads.createModal.referringCompany"),
        type: "select",
        options: referringCompanyOptions,
        showSearch: true,
        keepOrder: true,
        additionalProps: {
          initialSearchTerm: referringCompanySearchTerm,
          onSearch: onReferringCompanySearch,
          isLoading: isLoadingReferringCompanies,
          fixedOptions: [otherCompanyOption],
          showFixedOptions: true,
          entity: EntityType.COMPANY
        }
      };

      if (isOtherReferringCompanySelected) {
        conditionalFields.otherCompanyName = {
          label: t("leads.createModal.referringCompanyName"),
          type: "editableInput",
          initialValue: props.lead?.otherCompanyName || "",
          keepOrder: true
        };
      }

      const shouldShowReferringPerson =
        (!isEmpty(selectedReferringCompanyId) &&
          !isOtherReferringCompanySelected) ||
        isOtherReferringCompanySelected;

      if (shouldShowReferringPerson) {
        const otherPersonOption = {
          id: OTHER_PERSON_OPTION_VALUE,
          displayName: t("other")
        };

        conditionalFields.referringPerson = {
          label: t("leads.createModal.referringPerson"),
          type: "select",
          options: referringPersonOptions,
          showSearch: true,
          keepOrder: true,
          additionalProps: {
            initialSearchTerm: referringPersonSearchTerm,
            onSearch: onReferringPersonSearch,
            isLoading: isLoadingReferringPeople,
            fixedOptions: [otherPersonOption],
            showFixedOptions: true,
            entity: EntityType.PERSON
          }
        };

        if (isOtherReferringPersonSelected) {
          conditionalFields.otherPersonName = {
            label: t("leads.createModal.referringPersonName"),
            type: "editableInput",
            initialValue: props.lead?.otherPersonName || "",
            keepOrder: true
          };
        }
      }
    }

    return conditionalFields;
  }, [
    selectedSource,
    selectedReferringCompanyId,
    isOtherReferringCompanySelected,
    isOtherReferringPersonSelected,
    referringCompanyOptions,
    referringPersonOptions,
    referringCompanySearchTerm,
    referringPersonSearchTerm,
    isLoadingReferringCompanies,
    isLoadingReferringPeople,
    t,
    props.lead?.otherCompanyName,
    props.lead?.otherPersonName,
    inboundCampaignTypes,
    outboundCampaignTypes
  ]);

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "leads",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      if (props.onUpdateField) {
        const isMultiSelect = currentField.displayType === "multiSelect";

        if (isMultiSelect) {
          const currentValue = get(
            props.lead,
            currentFieldForNewOption.fieldKey
          );
          const currentValues = Array.isArray(currentValue) ? currentValue : [];
          const newValues = [...currentValues, optionName];
          props.onUpdateField(currentFieldForNewOption.fieldKey, newValues);
        } else {
          props.onUpdateField(currentFieldForNewOption.fieldKey, optionName);
        }
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const handleFieldUpdate = (
    field: string,
    newValue: string | number | string[],
    additionalFields?: { [fieldName: string]: string | number }
  ) => {
    if (field === "source") {
      const newSourceType = Number(newValue) as LeadType;
      setSelectedSource(newSourceType);

      setSelectedReferringCompanyId("");
      setIsOtherReferringCompanySelected(false);
      setIsOtherReferringPersonSelected(false);
      if (props.onUpdateField) {
        props.onUpdateField(field, newValue, {
          referringCompany: null,
          referringPerson: null,
          otherCompanyName: null,
          otherPersonName: null
        } as any);
      }
      return;
    }

    if (field === "referringCompany") {
      const isOther = newValue === OTHER_COMPANY_OPTION_VALUE;
      setIsOtherReferringCompanySelected(isOther);
      setSelectedReferringCompanyId(isOther ? "" : (newValue as string));
      setIsOtherReferringPersonSelected(false);
      if (props.onUpdateField) {
        props.onUpdateField(field, newValue, { referringPerson: null } as any);
      }
      return;
    }

    if (field === "referringPerson") {
      const isOther = newValue === OTHER_PERSON_OPTION_VALUE;
      setIsOtherReferringPersonSelected(isOther);
      if (props.onUpdateField) {
        if (!isOther) {
          props.onUpdateField(field, newValue, {
            otherPersonName: null
          } as any);
        } else {
          props.onUpdateField(field, newValue);
        }
      }
      return;
    }

    if (props.onUpdateField) {
      props.onUpdateField(field, newValue, additionalFields);
    }
  };

  const context: DetailFieldsContext = {
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption,
    onUpdateField: handleFieldUpdate
  };

  const { fieldsConfig: baseFieldsConfig, sections: baseSections } =
    useEntityDetailFieldsLayout(EntityType.LEAD, props.lead, context, {
      groupBySection: !props.isQuickView
    });

  // The conditional fields render right after `source`, in whichever group
  // (unsectioned or a section) holds it; every other group drops them
  const { fieldsConfig, sections } = useMemo(() => {
    const withoutConditionalFields = (config: FieldsConfig): FieldsConfig =>
      Object.fromEntries(
        Object.entries(config).filter(
          ([key]) => !CONDITIONAL_FIELD_KEYS.includes(key)
        )
      );

    const withConditionalFieldsAfterSource = (
      config: FieldsConfig
    ): FieldsConfig => {
      const orderedConfig: FieldsConfig = {};
      Object.entries(withoutConditionalFields(config)).forEach(
        ([key, fieldConfig]) => {
          orderedConfig[key] = fieldConfig;
          if (key === "source") {
            Object.assign(orderedConfig, getConditionalFields);
          }
        }
      );
      return orderedConfig;
    };

    const isSourceInSection = baseSections.some(
      (section) => "source" in section.fieldsConfig
    );
    let unsectionedConfig: FieldsConfig;
    if ("source" in baseFieldsConfig) {
      unsectionedConfig = withConditionalFieldsAfterSource(baseFieldsConfig);
    } else if (isSourceInSection) {
      unsectionedConfig = withoutConditionalFields(baseFieldsConfig);
    } else {
      unsectionedConfig = {
        ...withoutConditionalFields(baseFieldsConfig),
        ...getConditionalFields
      };
    }

    return {
      fieldsConfig: unsectionedConfig,
      sections: mapSectionsFieldsConfig(baseSections, (config) =>
        "source" in config
          ? withConditionalFieldsAfterSource(config)
          : withoutConditionalFields(config)
      )
    };
  }, [baseFieldsConfig, baseSections, getConditionalFields]);

  return (
    <>
      <DetailFields
        entity={props.lead}
        fieldsConfig={fieldsConfig}
        sections={sections}
        onUpdateField={handleFieldUpdate}
        className={props.className}
        containerClassName={props.containerClassName}
        tPrefix="leads.leadView"
        isDrawerOpen={props.isDrawerOpen}
        isQuickView={props.isQuickView}
        context={context}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => {
          setShowCreateOptionModal(false);
          setNewOptionName("");
          setCurrentFieldForNewOption(null);
        }}
        onCreateOption={context.handleCreateNewOption || (async () => {})}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
      />
    </>
  );
};

export default LeadDetailFields;
